import styles from './HubCompassRose.module.css';

export interface HubCompassRoseProps {
  size?: number;
  className?: string;
}

/**
 * ROSA DE LOS VIENTOS ORNAMENTADA — la del bloque "Azrahel del Mayor" del poster.
 *
 * POR QUE ES LOCAL DE ESTA PAGINA y no una variante de `SacredGeometry`. El
 * `variant="compass"` compartido dibuja un circulo con una cruz y un aro: en el
 * render se leia como una marca de agua tenue y en el poster es un ORNAMENTO
 * PROTAGONISTA — estrella de 8 puntas con la punta norte alargada, doble aro,
 * malla de rayos y una corona de puntos. Cambiar el componente compartido tocaria
 * `/buenos-aires` y el placeholder de obra, que usan `variant="flower"` del mismo
 * modulo, y hay cinco agentes en vuelo (misma regla que el ORACLE aplico a
 * `VipCrown`: si el hub necesita otra pieza, se hace local).
 *
 * Medido en `DEMO_0_Hub.png`: circulo de ~136 px de diametro centrado en
 * x 940 / y 1345, o sea el 13 % del ancho del poster.
 */
export function HubCompassRose({ size = 168, className }: HubCompassRoseProps) {
  // Rayos de la malla interior: 16 radios, los pares mas cortos.
  const rayos = Array.from({ length: 16 }, (_, i) => {
    const a = (i * Math.PI) / 8;
    const r = i % 2 === 0 ? 41 : 30;
    return { x1: 50 + Math.cos(a) * 6, y1: 50 + Math.sin(a) * 6, x2: 50 + Math.cos(a) * r, y2: 50 + Math.sin(a) * r };
  });

  // Corona de puntos sobre el aro exterior.
  const puntos = Array.from({ length: 24 }, (_, i) => {
    const a = (i * Math.PI) / 12;
    return { cx: 50 + Math.cos(a) * 46.5, cy: 50 + Math.sin(a) * 46.5 };
  });

  /** Estrella de 4 puntas inscrita, con los flancos concavos. */
  const punta = (rl: number, rc: number) =>
    `M50 ${50 - rl} Q${50 + rc} ${50 - rc} ${50 + rl} 50 Q${50 + rc} ${50 + rc} 50 ${50 + rl} ` +
    `Q${50 - rc} ${50 + rc} ${50 - rl} 50 Q${50 - rc} ${50 - rc} 50 ${50 - rl}Z`;

  return (
    <svg
      className={`${styles.rose} ${className ?? ''}`}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles.hairline}>
        <circle cx="50" cy="50" r="48" />
        <circle cx="50" cy="50" r="43" />
        <circle cx="50" cy="50" r="20.5" />
        {rayos.map((r) => (
          <line key={`${r.x2}-${r.y2}`} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} />
        ))}
      </g>

      {puntos.map((p) => (
        <circle key={`${p.cx}-${p.cy}`} className={styles.punto} cx={p.cx} cy={p.cy} r="0.85" />
      ))}

      {/* Estrella de 8 puntas = dos de 4, la segunda girada 45 grados y mas corta. */}
      <path className={styles.estrellaSecundaria} d={punta(29, 5.5)} transform="rotate(45 50 50)" />
      <path className={styles.estrella} d={punta(41, 7)} />
      <circle className={styles.nucleo} cx="50" cy="50" r="3.2" />
    </svg>
  );
}
